const m = require('minthril');
const { component } = require('../../component');
const { navigate } = require('../../routes');
const { formatActivityDuration } = require('./format');
const { routeActivityEvents } = require('./activity');

const periodOptions = [
  { label: 'Week', hours: 7 * 24 },
  { label: 'Day', hours: 24 },
  { label: 'Hour', hours: 1 },
  { label: 'Minute', hours: 1 / 60 }
];

function eventTime(event) {
  return eventDate(event)?.toLocaleString?.() || 'Just now';
}

function eventDate(event) {
  const timestamp = event.occurredAt;

  if (timestamp?.toDate) {
    return timestamp.toDate();
  }

  if (timestamp) {
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function eventSummary(event) {
  if (event.activityCount) {
    return `${event.activityCount} ${event.activityLabel}`;
  }

  if (event.outcome === 'failed') {
    return event.errorMessage || 'Render failed';
  }

  if (event.type === 'served') {
    const summaries = {
      cache_source_unchanged: 'served cache, source unchanged',
      cache_stale_refreshing: 'served stale cache, refresh queued',
      cache_ttl: 'served cache within TTL',
      cache_miss_crawler_rendered: 'rendered for crawler',
      cache_miss_human_origin: 'served origin to human',
      cache_miss_crawler_origin: 'served origin after render failure'
    };

    return summaries[event.outcome] || String(event.outcome || 'served').replaceAll('_', ' ');
  }

  return `${event.outcome || 'refresh'} · ${String(event.primaryReason || 'unknown').replaceAll('_', ' ')}`;
}

function eventColour(event) {
  if (event.outcome === 'failed') {
    return 'route-timeline__bar--failed';
  }

  if (event.type === 'served') {
    return 'route-timeline__bar--served';
  }

  if (event.outcome === 'validated') {
    return 'route-timeline__bar--validated';
  }

  return 'route-timeline__bar--refresh';
}

function eventDuration(event) {
  return event.renderDurationMs || event.validationDurationMs || event.durationMs || 0;
}

function eventDurationLabel(event) {
  const duration = eventDuration(event);

  if (event.activityCount) {
    const label = event.type === 'served' ? 'Average proxy response' :
      event.type === 'validation' ? 'Average validation' : 'Average render';
    return duration ? `${label} · ${duration.toFixed(0)} ms` : label;
  }

  if (event.type === 'served') {
    return `Proxy response · ${duration} ms`;
  }

  if (event.outcome === 'validated') {
    return `Validation · ${duration} ms`;
  }

  return `Render · ${duration} ms`;
}

function eventRequestType(event) {
  if (event.requestType) {
    return event.requestType;
  }

  if (event.type === 'refresh') {
    return event.trigger === 'crawler_miss' || event.trigger === 'human_miss'
      ? event.trigger.replace('_miss', '')
      : 'background';
  }

  return 'unknown';
}

function durationMaximum(value) {
  const magnitude = 10 ** Math.floor(Math.log10(value || 1));
  const normalizedValue = value / magnitude;
  const step = normalizedValue <= 1 ? 1 : normalizedValue <= 2 ? 2 : normalizedValue <= 5 ? 5 : 10;
  return step * magnitude;
}

const RouteSearch = component((vnode, emit) => {
  let search = '';

  return {
    view: ({ attrs: { title = 'Route activity', routes, detailsHref } }) => {
      const availableRoutes = Array.isArray(routes) ? routes : [];
      const listId = `route-search-${detailsHref.replace(/[^a-z0-9]/gi, '-')}`;

      function selectRoute(event) {
        const selectedPath = String(event.target.value || '');
        const selectedRoute = availableRoutes.find(route => route.path === selectedPath);

        if (selectedRoute) {
          navigate(`${detailsHref}?route=${encodeURIComponent(selectedRoute.path)}`);
        }
      }

      return m('section.route-search', [
        m('h3', title),
        m('input.route-search__input', {
          type: 'search',
          list: listId,
          autocomplete: 'off',
          value: search,
          placeholder: 'Choose or search for a route',
          oninput: event => {
            search = event.target.value || '';
            emit('redraw');
          },
          onchange: selectRoute,
          onkeydown: event => {
            if (event.key === 'Enter') {
              selectRoute(event);
            }
          }
        }),
        m('datalist', { id: listId }, availableRoutes.map(route =>
          m('option', { key: route.path, value: route.path })
        ))
      ]);
    }
  };
});

const RouteTimeline = component((vnode, emit) => {
  let events = [];
  let activity = [];
  let cursor = null;
  let hasMore = false;
  let loading = false;
  let loadingMore = false;
  let loadedPath = null;
  let activePath = null;
  let activeLoadMore = null;
  let loadMoreObserver = null;
  let hoveredEventId = null;
  let windowStart = null;
  let windowEnd = null;
  let dragStart = null;
  let dragEnd = null;
  let periodHours = 7 * 24;
  let previousPeriodHours = null;

  function hoverEvent(eventId) {
    hoveredEventId = eventId;
    emit('redraw');
  }

  function timeAtPointer(event, earliest, latest) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const svgPosition = (event.clientX - bounds.left) / bounds.width * 720;

    if (svgPosition < 42 || svgPosition > 692) {
      return null;
    }

    const position = (svgPosition - 42) / 650;
    return earliest + position * (latest - earliest);
  }

  function startDrag(event, earliest, latest) {
    event.preventDefault();
    dragStart = timeAtPointer(event, earliest, latest);

    if (dragStart === null) {
      return;
    }

    dragEnd = dragStart;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    emit('redraw');
  }

  function updateDrag(event, earliest, latest) {
    if (dragStart === null) {
      return;
    }

    event.preventDefault();
    const time = timeAtPointer(event, earliest, latest);

    if (time === null) {
      return;
    }

    dragEnd = time;
    emit('redraw');
  }

  function finishDrag(event) {
    if (dragStart === null) {
      return;
    }

    if (Math.abs(dragEnd - dragStart) > 1_000) {
      windowStart = Math.min(dragStart, dragEnd);
      windowEnd = Math.max(dragStart, dragEnd);
    }

    dragStart = null;
    dragEnd = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    emit('redraw');
  }

  function zoomOut(earliest, latest) {
    const currentStart = windowStart || earliest;
    const currentEnd = windowEnd || latest;
    const duration = currentEnd - currentStart;
    const expandedDuration = Math.min(latest - earliest, duration * 2);
    const centre = currentStart + duration / 2;
    windowStart = Math.max(earliest, centre - expandedDuration / 2);
    windowEnd = Math.min(latest, windowStart + expandedDuration);
    windowStart = Math.max(earliest, windowEnd - expandedDuration);
    emit('redraw');
  }

  function moveToNow(earliest, latest) {
    const duration = (windowEnd || latest) - (windowStart || earliest);
    windowEnd = latest;
    windowStart = Math.max(earliest, latest - duration);
    emit('redraw');
  }

  async function load(path, onLoad) {
    loading = true;
    events = [];
    activity = [];
    cursor = null;
    hasMore = false;
    loadedPath = null;
    emit('redraw');

    try {
      const result = await onLoad(path);
      events = result.events || [];
      activity = result.activity || [];
      cursor = result.cursor;
      hasMore = result.hasMore;
      windowStart = null;
      windowEnd = null;
      previousPeriodHours = null;
    } catch (error) {
      events = [];
      activity = [];
    } finally {
      loadedPath = path;
      loading = false;
      emit('redraw');
    }
  }

  async function loadMore() {
    if (loadingMore || !hasMore || !cursor || !activeLoadMore || !activePath) return;
    loadingMore = true;
    emit('redraw');

    try {
      const result = await activeLoadMore(activePath, cursor);
      events = events.concat(result.events || []);
      cursor = result.cursor;
      hasMore = result.hasMore;
    } catch (error) {
      console.error('Unable to load more route activity events.', error);
    } finally {
      loadingMore = false;
      emit('redraw');
    }
  }

  function observeLoadMore(element) {
    loadMoreObserver?.disconnect();
    if (!hasMore || typeof IntersectionObserver === 'undefined') return;
    loadMoreObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) loadMore();
    }, { root: element.parentElement, rootMargin: '120px' });
    loadMoreObserver.observe(element);
  }

  function selectPeriod(hours, earliest, latest) {
    periodHours = hours;
    windowEnd = latest;
    windowStart = latest - hours * 60 * 60 * 1000;
    emit('redraw');
  }

  return {
    oninit: ({ attrs: { path, onLoad, onLoadMore } }) => {
      activePath = path;
      activeLoadMore = onLoadMore;
      load(path, onLoad);
    },
    onbeforeupdate: ({ attrs: { path, onLoad, onLoadMore } }) => {
      activeLoadMore = onLoadMore;
      if (path !== loadedPath && !loading) {
        activePath = path;
        load(path, onLoad);
      }
    },
    view: ({ attrs: { path, onLoadMore } }) => {
      activePath = path;
      activeLoadMore = onLoadMore;
      const activityEvents = routeActivityEvents(activity);
      const allEvents = (activityEvents.length ? activityEvents : events)
        .map(event => ({ ...event, date: eventDate(event) || new Date(), durationMs: eventDuration(event) }))
        .sort((left, right) => left.date.getTime() - right.date.getTime());
      const fullEarliest = allEvents?.[0]?.date.getTime() || Date.now();
      const fullLatest = Date.now();

      if (allEvents?.length && previousPeriodHours === null) {
        windowStart = fullLatest - periodHours * 60 * 60 * 1000;
        windowEnd = fullLatest;
        previousPeriodHours = periodHours;
      }

      const earliest = windowStart || fullEarliest;
      const latest = windowEnd || fullLatest;
      const chartEvents = allEvents.filter(event => {
        const occurredAt = event.date.getTime();
        return occurredAt >= earliest && occurredAt <= latest;
      });
      const timeRange = Math.max(1, latest - earliest);
      const maximumDuration = durationMaximum(Math.max(1, ...chartEvents.map(event => event.durationMs)));
      const activeEventId = hoveredEventId;
      const isZoomed = earliest !== fullEarliest || latest !== fullLatest;

      return m('section.route-timeline', [
        m('h3', `Activity · ${formatActivityDuration(latest - earliest)} · ${path}`),
        loading ? m('p.muted', 'Loading route activity…') :
          (allEvents.length || events.length ? [
            m('div.route-timeline__controls', [
              periodOptions.map(option => m('button', {
                type: 'button',
                class: `button button--quiet route-timeline__period${option.hours === periodHours ? ' route-timeline__period--current' : ''}`,
                onclick: () => selectPeriod(option.hours, fullEarliest, fullLatest)
              }, option.label)),
              m('button', {
                type: 'button',
                class: 'button button--quiet',
                disabled: !isZoomed,
                onclick: () => zoomOut(fullEarliest, fullLatest)
              }, 'Zoom out'),
              m('button', {
                type: 'button',
                class: 'button button--quiet',
                disabled: latest >= fullLatest,
                onclick: () => moveToNow(fullEarliest, fullLatest)
              }, 'Now')
            ]),
            m('p.route-timeline__legend', [
              m('span', [m('i.route-timeline__key.route-timeline__key--served'), 'Served']),
              m('span', [m('i.route-timeline__key.route-timeline__key--refresh'), 'Rendered']),
              m('span', [m('i.route-timeline__key.route-timeline__key--validated'), 'Validated']),
              m('span', [m('i.route-timeline__key.route-timeline__key--failed'), 'Failed'])
            ]),
            m('div.route-timeline__plot', [
              m('svg.route-timeline__chart', {
                viewBox: '0 0 720 140',
                role: 'img',
                'aria-label': `Recent activity for ${path}`,
                onpointerdown: event => startDrag(event, earliest, latest),
                onpointermove: event => updateDrag(event, earliest, latest),
                onpointerup: finishDrag,
                onpointercancel: finishDrag
              }, [
                m('line.route-timeline__axis', { x1: 42, x2: 692, y1: 108, y2: 108 }),
                [0, 0.5, 1].map(fraction => {
                  const y = 108 - fraction * 80;
                  return [
                    m('line.route-timeline__gridline', { x1: 42, x2: 692, y1: y, y2: y }),
                    m('text.route-timeline__value', {
                      x: 34,
                      y: y + 4,
                      'text-anchor': 'end'
                    }, `${Math.round(maximumDuration * fraction)} ms`)
                  ];
                }),
                dragStart !== null ? m('rect.route-timeline__selection', {
                  x: 42 + (Math.min(dragStart, dragEnd) - earliest) / timeRange * 650,
                  y: 28,
                  width: Math.abs(dragEnd - dragStart) / timeRange * 650,
                  height: 80
                }) : null,
                chartEvents.map(event => {
                  const eventPosition = event.plotAt || event.date.getTime();
                  const position = chartEvents.length === 1
                    ? 0.5
                    : (eventPosition - earliest) / timeRange;
                  const x = 42 + position * 650;
                  const y = 108 - event.durationMs / maximumDuration * 80;
                  const active = event.id === activeEventId;

                  return m('g', { key: event.id }, [
                    m('rect', {
                      x: x - 4,
                      y,
                      width: 8,
                      height: Math.max(2, 108 - y),
                      tabindex: 0,
                      role: 'graphics-symbol',
                      'aria-label': `${eventTime(event)}: ${eventSummary(event)}`,
                      class: `route-timeline__bar ${eventColour(event)}${active ? ' route-timeline__bar--active' : ''}`,
                      onmouseenter: () => hoverEvent(event.id),
                      onmouseleave: () => hoverEvent(null),
                      onfocus: () => hoverEvent(event.id),
                      onblur: () => hoverEvent(null),
                    }, m('title', `${eventTime(event)}: ${eventSummary(event)}`))
                  ]);
                }),
                m('text.route-timeline__label', { x: 42, y: 132 },
                  new Date(earliest).toLocaleDateString()),
                latest > earliest ? m('text.route-timeline__label', {
                  x: 692,
                  y: 132,
                  'text-anchor': 'end'
                }, new Date(latest).toLocaleDateString()) : null
              ]),
            ]),
            m('ol.route-timeline__events', [
              ...events.map(event =>
                m('li', {
                  key: event.id,
                  class: event.id === activeEventId ? 'route-timeline__event--active' : '',
                  onmouseenter: () => hoverEvent(event.id),
                  onmouseleave: () => hoverEvent(null)
                }, [
                  m('time', eventTime(event)),
                  m('strong', eventSummary(event)),
                  m('span.muted', eventDurationLabel(event)),
                  m('span.muted', `Triggered by · ${eventRequestType(event)}`),
                  event.dependencyResults?.length ? m('ul', event.dependencyResults.map(result =>
                    m('li', { key: `${result.method}:${result.url}` },
                      `${result.type} · ${result.result} · ${result.url}`)
                  )) : null
                ])
              ),
              hasMore ? m('li.route-timeline__load-more', {
                key: 'load-more',
                oncreate: ({ dom }) => observeLoadMore(dom),
                onupdate: ({ dom }) => observeLoadMore(dom)
              }, loadingMore ? 'Loading more activity…' :
                typeof IntersectionObserver === 'undefined'
                  ? m('button.button.button--quiet', {
                    type: 'button',
                    onclick: loadMore
                  }, 'Load more activity')
                  : 'Scroll for more activity') : null
            ])
          ] : m('p.muted', 'No recent activity for this route.'))
      ]);
    },
    onremove: () => loadMoreObserver?.disconnect()
  };
});

module.exports = { RouteSearch, RouteTimeline };
